import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Package, Wrench } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const QuoteModal = ({ open, onOpenChange, preSelectedBrand = '', preSelectedService = '' }) => {
  // Determine initial service state based on preSelectedService
  const getInitialServiceState = () => {
    if (preSelectedService === 'order-part' || preSelectedService === 'servicing') {
      return preSelectedService;
    }
    return 'select';
  };

  // Service selection state - 'select', 'order-part', or 'servicing'
  const [selectedService, setSelectedService] = useState(getInitialServiceState());
  
  // Track if service was pre-selected (to hide back button)
  const [isDirectAccess, setIsDirectAccess] = useState(!!preSelectedService);
  
  // Order Part State
  const [orderPartData, setOrderPartData] = useState({
    brand: preSelectedBrand || '',
    year: '',
    model: '',
    partName: '',
  });

  // Servicing State
  const [servicingData, setServicingData] = useState({
    customerName: '',
    date: null,
    time: '',
    details: '',
    brand: preSelectedBrand || '',
  });

  // Reset modal when opened with new props
  useEffect(() => {
    if (open) {
      // Update service selection based on preSelectedService
      if (preSelectedService === 'order-part') {
        setSelectedService('order-part');
        setOrderPartData(prev => ({ ...prev, brand: preSelectedBrand || '' }));
      } else if (preSelectedService === 'servicing') {
        setSelectedService('servicing');
        setServicingData(prev => ({ ...prev, brand: preSelectedBrand || '' }));
      } else {
        setSelectedService('select');
      }
    } else {
      // Reset to selection screen when modal closes
      setSelectedService('select');
      setOrderPartData({ brand: '', year: '', model: '', partName: '' });
      setServicingData({ customerName: '', date: null, time: '', details: '', brand: '' });
    }
  }, [open, preSelectedBrand, preSelectedService]);

  // Brands list
  const brands = [
    'Toyota', 'Honda', 'Suzuki', 'Mazda', 'Nissan', 'Daihatsu', 'Mitsubishi',
    'Subaru', 'Lexus', 'Isuzu', 'Hyundai', 'KIA', 'FAW', 'Changan', 'DFSK',
    'MG', 'Chery', 'Prince', 'Haval', 'Geely', 'BYD', 'Proton', 'United',
    'Mercedes-Benz', 'BMW', 'Audi', 'Volkswagen'
  ];

  // Generate years from 2000 to current year + 1
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1999 }, (_, i) => currentYear - i + 1);

  // Time slots from 9 AM to 9 PM
  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM',
    '05:00 PM', '06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM'
  ];

  // Handle Order Part submission
  const handleOrderPartSubmit = (e) => {
    e.preventDefault();
    
    if (!orderPartData.brand || !orderPartData.year || !orderPartData.model || !orderPartData.partName) {
      toast.error('Please fill all fields');
      return;
    }

    const message = `*Order Part Request*\n\nBrand: ${orderPartData.brand}\nYear: ${orderPartData.year}\nModel: ${orderPartData.model}\nPart Name: ${orderPartData.partName}`;
    const whatsappUrl = `https://wa.me/923017928133?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Reset form
    setOrderPartData({ brand: '', year: '', model: '', partName: '' });
    setSelectedService('select');
    toast.success('Redirecting to WhatsApp...');
    onOpenChange(false);
  };

  // Handle Servicing submission
  const handleServicingSubmit = (e) => {
    e.preventDefault();
    
    if (!servicingData.customerName || !servicingData.date || !servicingData.time || !servicingData.details) {
      toast.error('Please fill all fields');
      return;
    }

    const formattedDate = format(servicingData.date, 'dd/MM/yyyy');
    const brandInfo = servicingData.brand ? `\nBrand: ${servicingData.brand}` : '';
    const message = `*Servicing Request*\n\nCustomer Name: ${servicingData.customerName}${brandInfo}\nDate: ${formattedDate}\nTime: ${servicingData.time}\nDetails: ${servicingData.details}`;
    const whatsappUrl = `https://wa.me/923017928133?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Reset form
    setServicingData({ customerName: '', date: null, time: '', details: '', brand: '' });
    setSelectedService('select');
    toast.success('Redirecting to WhatsApp...');
    onOpenChange(false);
  };

  // Disable past dates and Sundays
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Disable if date is in the past
    if (date < today) return true;
    
    // Disable if date is Sunday (0 = Sunday)
    if (date.getDay() === 0) return true;
    
    return false;
  };

  // Handle back button
  const handleBack = () => {
    setSelectedService('select');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Service Request</DialogTitle>
          <DialogDescription>
            {selectedService === 'select' 
              ? 'Choose a service below to get started'
              : 'Fill in your details and we\'ll get back to you on WhatsApp'}
          </DialogDescription>
        </DialogHeader>

        {/* Service Selection Buttons */}
        {selectedService === 'select' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-6">
            <Button
              onClick={() => setSelectedService('order-part')}
              className="h-32 flex flex-col items-center justify-center gap-3 bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              <Package size={40} />
              <span className="text-lg font-semibold">Order a Part</span>
            </Button>
            <Button
              onClick={() => setSelectedService('servicing')}
              className="h-32 flex flex-col items-center justify-center gap-3 bg-primary hover:bg-primary/90 text-primary-foreground"
              size="lg"
            >
              <Wrench size={40} />
              <span className="text-lg font-semibold">Servicing</span>
            </Button>
          </div>
        )}

        {/* Order Part Form */}
        {selectedService === 'order-part' && (
          <div className="mt-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="mb-4 text-muted-foreground hover:text-foreground"
            >
              ← Back
            </Button>
            <form onSubmit={handleOrderPartSubmit} className="space-y-4">
              {/* Brand */}
              <div className="space-y-2">
                <Label htmlFor="brand">Brand *</Label>
                <Select
                  value={orderPartData.brand}
                  onValueChange={(value) => setOrderPartData({ ...orderPartData, brand: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {brands.map((brand) => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Year */}
              <div className="space-y-2">
                <Label htmlFor="year">Year *</Label>
                <Select
                  value={orderPartData.year}
                  onValueChange={(value) => setOrderPartData({ ...orderPartData, year: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model */}
              <div className="space-y-2">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  placeholder="e.g., Corolla, Civic, Alto"
                  value={orderPartData.model}
                  onChange={(e) => setOrderPartData({ ...orderPartData, model: e.target.value })}
                />
              </div>

              {/* Part Name */}
              <div className="space-y-2">
                <Label htmlFor="partName">Part Name *</Label>
                <Input
                  id="partName"
                  placeholder="e.g., Brake Pads, Engine Oil Filter"
                  value={orderPartData.partName}
                  onChange={(e) => setOrderPartData({ ...orderPartData, partName: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Send to WhatsApp
              </Button>
            </form>
          </div>
        )}

        {/* Servicing Form */}
        {selectedService === 'servicing' && (
          <div className="mt-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="mb-4 text-muted-foreground hover:text-foreground"
            >
              ← Back
            </Button>
            <form onSubmit={handleServicingSubmit} className="space-y-4">
              {/* Customer Name */}
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name *</Label>
                <Input
                  id="customerName"
                  placeholder="Enter your name"
                  value={servicingData.customerName}
                  onChange={(e) => setServicingData({ ...servicingData, customerName: e.target.value })}
                />
              </div>

              {/* Brand (Optional for servicing) */}
              {servicingData.brand && (
                <div className="space-y-2">
                  <Label htmlFor="servicingBrand">Brand</Label>
                  <Input
                    id="servicingBrand"
                    value={servicingData.brand}
                    disabled
                    className="bg-muted"
                  />
                </div>
              )}

              {/* Date Picker */}
              <div className="space-y-2">
                <Label>Appointment Date * (Closed on Sundays)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {servicingData.date ? format(servicingData.date, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={servicingData.date}
                      onSelect={(date) => setServicingData({ ...servicingData, date })}
                      disabled={isDateDisabled}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Slot */}
              <div className="space-y-2">
                <Label htmlFor="time">Preferred Time * (9:00 AM - 9:00 PM)</Label>
                <Select
                  value={servicingData.time}
                  onValueChange={(value) => setServicingData({ ...servicingData, time: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time slot" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {timeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Details */}
              <div className="space-y-2">
                <Label htmlFor="details">Service Details *</Label>
                <Textarea
                  id="details"
                  placeholder="Describe what service you need (e.g., Engine oil change, brake inspection, AC repair)"
                  rows={4}
                  value={servicingData.details}
                  onChange={(e) => setServicingData({ ...servicingData, details: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Send to WhatsApp
              </Button>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteModal;
